import React, { useEffect, useState } from "react";
import { useAppBlocksContext } from "../../../../context/AppBlocksContext";
import DetailsviewPreview from "../../../../components/BuilderComponents/Preview/DetailsviewPreview";
import { useParams } from "react-router-dom";
import fileFolderConfig from "../../../../commonFunctions/fileFolderConfig";
import RenderNestedGridLayout from "./RenderNestedGridLayout";
import BuilderRightSideBar from "../../../../Layouts/BuilderRightSideBar";
import EditDetailsviewFields from "./EditDetailsviewFields";
import BuilderModal from "../../../../Layouts/Modal/BuilderModal";
import TableTreeModal from "../../../../components/BuilderComponents/TableTreeModal";
import RecordsetListCardDesign from "./RecordsetListCardDesign";
import DetailviewHeader from "./DetailviewHeader";
import { toastError } from "../../../../utils/toastUtils";

const DetailviewLayoutEditor = ({
  dragFromOutside,
  setDragFromOutside,
  tableRef,
  tableID,
  tableType = "statemachine",
  activityID = "",
  device,
  elements = [],
  variables = [],
  headerComponent
}) => {
  const [allowEdit, setAllowEdit] = useState(false);
  const [previewData, setPreviewData] = useState([]);
  const { layout, getDetailviewConfig } = useAppBlocksContext();

  useEffect(() => {
    if (layout.length === 0) {
      setAllowEdit(true);
    }
    if (allowEdit) return;
    let detailviewConfig = getDetailviewConfig();
    detailviewConfig = detailviewConfig?.elements || [];
    setPreviewData(detailviewConfig);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowEdit]);
  return (
    <>
      <DetailviewHeader
        device={device}
        activityID={activityID}
        allowEdit={allowEdit}
        setAllowEdit={setAllowEdit}
        headerComponent={headerComponent}
      />
      {allowEdit ? (
        <DetailviewLayoutWithEdit
          dragFromOutside={dragFromOutside}
          setDragFromOutside={setDragFromOutside}
          tableRef={tableRef}
          device={device}
          tableID={tableID}
          tableType={tableType}
          activityID={activityID}
          elements={elements}
          variables={variables}
        />
      ) : (
        <DetailsviewPreview detailviewConfig={previewData} />
      )}
    </>
  );
};

const DetailviewLayoutWithEdit = ({
  dragFromOutside,
  setDragFromOutside,
  tableRef,
  tableID,
  tableType,
  activityID = "",
  device,
  elements = [],
  variables = []
}) => {
  const [editElementRightsidebar, setEditElementRightsidebar] = useState({ show: false, id: "" });
  const { layout, setLayout, layoutData, nestedGridData, selectedAppBlock } = useAppBlocksContext();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { blockId } = useParams();

  const editElement = (editElementData) => {
    let copyObj = layoutData.current[editElementData.i];
    const obj = {
      id: editElementData.i,
      type: copyObj.type,
      show: true,
      config: copyObj,
      elements: elements,
      data: editElementData.data
    };
    setEditElementRightsidebar(obj);
  };

  const handleModalAction = (response) => {
    if (response.type === "edit" || response.type === "tableview") {
      editElement(response);
    }
    if (response.type === "add" || response.type === "change" || response.type === "recordset_layout") {
      setIsModalOpen(response);
    }
  };

  const saveElement = (config) => {
    const newLayoutData = JSON.parse(JSON.stringify(layoutData.current));
    newLayoutData[config.i] = config;
    layoutData.current = newLayoutData;
    if (config.type === "html" || config.type === "dynamic-html") {
      setEditElementRightsidebar({ show: false, id: "" });
    } else {
      setLayout([...layout]);
    }
  };

  const editChangeField = (data) => {
    const nonSupportedField = ["lookup", "lookup-sm", "app-user"];
    if (nonSupportedField.includes(data.field_type)) {
      setIsModalOpen({ type: "" });
      toastError("Can not use field");
      return;
    }
    let i = isModalOpen.i;
    const newLayoutData = JSON.parse(JSON.stringify(layoutData.current));
    if (data.field_type === "recordset" || data.field_type === "recordset-list" || data.field_type === "grid") {
      data.config = {
        layouts: []
      };
    }
    const newData = { ...newLayoutData[i], ...data };
    newData.prefix = data.prefix || "";

    newLayoutData[i] = newData;
    layoutData.current = newLayoutData;
    setIsModalOpen({ type: "" });
    setLayout([...layout]);
  };

  const typeBasedSize = (data) => {
    let type = data.type;
    if (data.data) {
      //for tableview cell
      type = "cell";
    }
    switch (type) {
      case "detailview":
        return "large";
      case "html":
      case "button-group":
      case "dynamic-html":
      case "cell":
        return "medium";
      default:
        return "small";
    }
  };
  const handleSaveRecordsetLayout = (data) => {
    const newLayoutData = JSON.parse(JSON.stringify(layoutData.current));
    const index = isModalOpen.i;

    if (!newLayoutData[index]) {
      newLayoutData[index] = { config: {} };
    } else if (!newLayoutData[index].config) {
      newLayoutData[index].config = {};
    }

    newLayoutData[index].config.layouts = data;

    layoutData.current = newLayoutData;
    setLayout([...layout]);
    setIsModalOpen({ type: "" });
  };

  const makeVariableFolder = () => {
    if (selectedAppBlock.parentData.includes("detailsview") && blockId) {
      const allVariables = variables || [];
      const detailviewFolder = fileFolderConfig.getFolderConfig(
        "detailview_variables",
        "detailview variables",
        "detailview_variables",
        false,
        false,
        false
      );
      const newData = allVariables.reduce((acc, variable) => {
        detailviewFolder.items.push(
          fileFolderConfig.getFileConfig(variable.id, variable.display, "detailview_variables", false, false)
        );
        return {
          ...acc,
          [variable.id]: {
            id: variable.id,
            display: variable.display,
            type: "variable"
          }
        };
      }, {});
      return {
        tree: [detailviewFolder],
        data: newData
      };
    } else {
      return {
        tree: [],
        data: {}
      };
    }
  };

  return (
    <>
      <RenderNestedGridLayout
        dragFromOutside={dragFromOutside}
        setDragFromOutside={setDragFromOutside}
        nestedGridData={nestedGridData}
        layoutData={layoutData}
        tableRef={tableRef}
        handleModalAction={handleModalAction}
        layout={layout}
        setLayout={setLayout}
        device={device}
        tableID={tableID}
      />

      <BuilderRightSideBar
        isOpen={editElementRightsidebar.show}
        onClose={() => setEditElementRightsidebar({ show: false, id: "" })}
        heading={editElementRightsidebar.type}
        size={typeBasedSize(editElementRightsidebar)}
      >
        <EditDetailsviewFields
          editElementRightsidebar={editElementRightsidebar}
          saveElement={saveElement}
          smId={tableID}
          tableType={tableType}
          nestedGridData={nestedGridData}
          device={device}
          appendExtraFilesAndFolders={makeVariableFolder()}
        />
      </BuilderRightSideBar>

      <BuilderModal
        isOpen={isModalOpen.type}
        onClose={() => setIsModalOpen({ type: "" })}
        modalName={isModalOpen.modal_name}
      >
        {(isModalOpen.type === "add" || isModalOpen.type === "change") && (
          <TableTreeModal
            callbackFunction={editChangeField}
            tableId={tableID}
            folderId={activityID ? [activityID, "global_fields"] : []}
            selectedFile={isModalOpen.type === "change" ? layoutData.current[isModalOpen.i]?.id : ""}
            appendExtraFilesAndFolders={makeVariableFolder()}
          />
        )}
        {isModalOpen.type === "recordset_layout" && (
          <RecordsetListCardDesign
            tableRef={tableRef}
            layoutData={layoutData.current[isModalOpen.i]["config"]?.layouts || []}
            handleSaveRecordsetLayout={handleSaveRecordsetLayout}
            tableId={tableID}
            tableType={tableType}
            folderId={[layoutData.current[isModalOpen.i].id]}
            device={device}
          />
        )}
      </BuilderModal>
    </>
  );
};

export default DetailviewLayoutEditor;
export { DetailviewLayoutWithEdit };
